import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, ChevronDown, ExternalLink, Settings, Check, AlertCircle, X, Plus } from 'lucide-react';
import { DealerLocation, Dealer, ServiceBay, DealerService, Appointment } from '../types';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';


const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    
    h1 {
      margin: 0;
      font-size: 2rem;
      font-weight: 700;
      color: #1f2937;
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DealerInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .dealer-name {
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .dealer-details {
    font-size: 0.875rem;
    color: #0369a1;
  }
`;

const LocationSelector = styled.div`
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .location-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
  }
`;

const SelectorsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .selector-group {
    flex: 1;
    max-width: 300px;
    
    .selector-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #374151;
      margin-bottom: 8px;
    }
  }
`;

const LocationDropdown = styled.div`
  position: relative;
  display: inline-block;
  min-width: 250px;
`;

const BaySelector = styled.div`
  position: relative;
  display: inline-block;
  min-width: 200px;
`;

const BayDropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  .bay-info {
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: left;
    
    .bay-count {
      font-weight: 600;
    }
    
    .bay-text {
      color: #6b7280;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: #6b7280;
  }
`;

const BayDropdownMenu = styled.div<{ isOpen: boolean }>`
  display: ${(props: { isOpen: boolean }) => props.isOpen ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 4px;
  max-height: 300px;
  overflow-y: auto;
`;

const BayDropdownHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  
  .bay-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .bay-title {
      font-weight: 600;
      color: #374151;
      font-size: 0.875rem;
    }
    
    .bay-actions {
      display: flex;
      gap: 8px;
      
      button {
        padding: 4px 8px;
        border: 1px solid #d1d5db;
        background: white;
        color: #374151;
        border-radius: 4px;
        font-size: 0.75rem;
        cursor: pointer;
        transition: all 0.2s;
        
        &:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }
      }
    }
  }
`;

const BayDropdownItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${(props: { isSelected: boolean }) => props.isSelected ? '#f0f9ff' : 'white'};
  
  &:hover {
    background: ${(props: { isSelected: boolean }) => props.isSelected ? '#eff6ff' : '#f3f4f6'};
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  
  .checkbox {
    width: 18px;
    height: 18px;
    border: 2px solid ${(props: { isSelected: boolean }) => props.isSelected ? '#3b82f6' : '#d1d5db'};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props: { isSelected: boolean }) => props.isSelected ? '#3b82f6' : 'white'};
    flex-shrink: 0;
    
    svg {
      color: white;
      width: 12px;
      height: 12px;
    }
  }
  
  .bay-details {
    flex: 1;
    
    .bay-name {
      font-weight: 600;
      color: #1f2937;
    }
    
    .bay-description {
      font-size: 0.75rem;
      color: #6b7280;
      margin-top: 2px;
    }
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  .location-info {
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: left;
    
    .location-name {
      font-weight: 600;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: #6b7280;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  display: ${(props: { isOpen: boolean }) => props.isOpen ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 4px;
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: white;
  color: #374151;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #f3f4f6;
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  
  .location-name {
    font-weight: 600;
  }
  
  .location-address {
    font-size: 0.75rem;
    color: #6b7280;
  }
`;

const ThirdPartyBanner = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  
  .icon {
    color: #d97706;
    flex-shrink: 0;
  }
  
  .content {
    flex: 1;
    
    .title {
      font-weight: 600;
      color: #92400e;
      margin-bottom: 4px;
    }
    
    .description {
      font-size: 0.875rem;
      color: #a16207;
    }
  }
`;

const CalendarControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .date-navigation {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .current-week {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      min-width: 200px;
      text-align: center;
    }
  }
  
  .view-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ViewButton = styled.button<{ isActive?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${(props: { isActive?: boolean }) => props.isActive ? '#3b82f6' : '#d1d5db'};
  background: ${(props: { isActive?: boolean }) => props.isActive ? '#3b82f6' : 'white'};
  color: ${(props: { isActive?: boolean }) => props.isActive ? 'white' : '#374151'};
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${(props: { isActive?: boolean }) => props.isActive ? '#2563eb' : '#f3f4f6'};
    border-color: ${(props: { isActive?: boolean }) => props.isActive ? '#2563eb' : '#9ca3af'};
  }
`;

const CalendarContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  overflow: hidden;
  min-height: 650px;
`;

const CalendarHeader = styled.div<{ enabledBaysCount: number }>`
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
`;

const HeaderRow = styled.div<{ enabledBaysCount: number; dayCount: number }>`
  display: grid;
  grid-template-columns: 80px repeat(${(props: { enabledBaysCount: number; dayCount: number }) => props.dayCount * Math.max(1, props.enabledBaysCount)}, 1fr);
`;

const TimeColumn = styled.div`
  padding: 12px 8px;
  border-right: 1px solid #e5e7eb;
  font-weight: 600;
  color: #6b7280;
  font-size: 0.75rem;
  text-align: center;
`;

const DayHeaderCell = styled.div<{ isToday?: boolean; bayCount: number }>`
  grid-column: span ${(props: { isToday?: boolean; bayCount: number }) => props.bayCount};
  padding: 12px 8px 8px 8px;
  text-align: center;
  background: ${(props: { isToday?: boolean; bayCount: number }) => props.isToday ? '#eff6ff' : 'transparent'};
  border-right: 1px solid #e5e7eb;
  border-bottom: ${(props: { isToday?: boolean; bayCount: number }) => props.bayCount > 1 ? '1px solid #e5e7eb' : 'none'};
  
  &:last-child {
    border-right: none;
  }
  
  .day-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: ${(props: { isToday?: boolean; bayCount: number }) => props.isToday ? '#1d4ed8' : '#374151'};
    margin-bottom: 4px;
  }
  
  .day-number {
    font-size: 1.125rem;
    font-weight: 700;
    color: ${(props: { isToday?: boolean; bayCount: number }) => props.isToday ? '#1d4ed8' : '#1f2937'};
  }
`;

const BayHeaderCell = styled.div<{ isToday?: boolean; isLastBayInDay?: boolean }>`
  padding: 8px;
  text-align: center;
  background: ${(props: { isToday?: boolean; isLastBayInDay?: boolean }) => props.isToday ? '#eff6ff' : 'transparent'};
  border-right: ${(props: { isToday?: boolean; isLastBayInDay?: boolean }) => props.isLastBayInDay ? '1px solid #e5e7eb' : '1px solid #f3f4f6'};
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  
  &:last-child {
    border-right: none;
  }
`;

const CalendarGrid = styled.div<{ enabledBaysCount: number; dayCount: number }>`
  flex: 1;
  display: grid;
  grid-template-columns: 80px repeat(${(props: { enabledBaysCount: number; dayCount: number }) => props.dayCount * Math.max(1, props.enabledBaysCount)}, 1fr);
  overflow-y: auto;
`;

const TimeSlot = styled.div`
  padding: 8px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.75rem;
  color: #6b7280;
  text-align: right;
  background: #fafafa;
  min-height: 60px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
`;

const BayColumn = styled.div<{ isToday?: boolean; isLastBay?: boolean }>`
  border-right: ${(props: { isToday?: boolean; isLastBay?: boolean }) => props.isLastBay ? '1px solid #e5e7eb' : '1px solid #f3f4f6'};
  border-bottom: 1px solid #f3f4f6;
  min-height: 60px;
  background: ${(props: { isToday?: boolean; isLastBay?: boolean }) => props.isToday ? '#fefbff' : 'white'};
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${(props: { isToday?: boolean; isLastBay?: boolean }) => props.isToday ? '#f0f9ff' : '#f9fafb'};
  }
  
  .bay-label {
    position: absolute;
    top: 2px;
    left: 4px;
    font-size: 0.625rem;
    color: #9ca3af;
    font-weight: 500;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyStateContainer = styled.div`
  position: relative;
  opacity: 0.3;
  pointer-events: none;
`;

const EmptyStateMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 24px 32px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid #e5e7eb;
  z-index: 10;
  opacity: 1;
  pointer-events: auto;
  
  .message-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: #f3f4f6;
    border-radius: 50%;
    margin: 0 auto 16px;
    color: #6b7280;
  }
  
  .message-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #374151;
    text-align: center;
    margin-bottom: 8px;
  }
  
  .message-text {
    font-size: 0.875rem;
    color: #6b7280;
    text-align: center;
  }
`;

// Month view specific components
const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  height: 600px;
`;

const MonthDay = styled.div<{ isCurrentMonth?: boolean; isToday?: boolean; hasAppointments?: boolean }>`
  background: ${(props: { isCurrentMonth?: boolean; isToday?: boolean; hasAppointments?: boolean }) => 
    props.isToday ? '#eff6ff' : (props.isCurrentMonth ? 'white' : '#f9fafb')};
  padding: 8px;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${(props: { isCurrentMonth?: boolean; isToday?: boolean; hasAppointments?: boolean }) => 
      props.isToday ? '#dbeafe' : '#f3f4f6'};
  }
  
  .day-number {
    font-size: 0.875rem;
    font-weight: ${(props: { isCurrentMonth?: boolean; isToday?: boolean; hasAppointments?: boolean }) => 
      props.isToday ? '700' : '600'};
    color: ${(props: { isCurrentMonth?: boolean; isToday?: boolean; hasAppointments?: boolean }) => 
      props.isToday ? '#1d4ed8' : (props.isCurrentMonth ? '#1f2937' : '#9ca3af')};
    margin-bottom: 4px;
  }
  
  .appointment-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #3b82f6;
    color: white;
    border-radius: 12px;
    padding: 2px 6px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-top: auto;
    min-height: 20px;
    
    &:hover {
      background: #2563eb;
    }
  }
`;

const ClickableDayHeader = styled.div<{ isToday?: boolean }>`
  padding: 12px 8px 8px 8px;
  text-align: center;
  background: ${(props: { isToday?: boolean }) => props.isToday ? '#eff6ff' : 'transparent'};
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${(props: { isToday?: boolean }) => props.isToday ? '#dbeafe' : '#f3f4f6'};
  }
  
  &:last-child {
    border-right: none;
  }
  
  .day-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: ${(props: { isToday?: boolean }) => props.isToday ? '#1d4ed8' : '#374151'};
    margin-bottom: 4px;
  }
  
  .day-number {
    font-size: 1.125rem;
    font-weight: 700;
    color: ${(props: { isToday?: boolean }) => props.isToday ? '#1d4ed8' : '#1f2937'};
  }
`;

// Modal components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${(props: { isOpen: boolean }) => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const FormSection = styled.div`
  margin-bottom: 24px;
  
  .section-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const FormGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props: { columns?: number }) => props.columns || 2}, 1fr);
  gap: 16px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  
  label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
  }
  
  input, select {
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    transition: border-color 0.2s;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const ServiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
`;

const ServiceItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 2px solid ${(props: { isSelected: boolean }) => props.isSelected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  background: ${(props: { isSelected: boolean }) => props.isSelected ? '#eff6ff' : 'white'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${(props: { isSelected: boolean }) => props.isSelected ? '#2563eb' : '#9ca3af'};
    background: ${(props: { isSelected: boolean }) => props.isSelected ? '#dbeafe' : '#f9fafb'};
  }
  
  .checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid ${(props: { isSelected: boolean }) => props.isSelected ? '#3b82f6' : '#d1d5db'};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props: { isSelected: boolean }) => props.isSelected ? '#3b82f6' : 'white'};
    flex-shrink: 0;
    
    svg {
      color: white;
      width: 12px;
      height: 12px;
    }
  }
  
  .service-info {
    flex: 1;
    
    .service-name {
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 2px;
    }
    
    .service-details {
      font-size: 0.875rem;
      color: #6b7280;
      display: flex;
      gap: 16px;
    }
  }
`;

const InfoMessage = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 8px 12px;
  border-radius: 6px;
  margin-top: 12px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${(props: { variant?: 'primary' | 'secondary' }) => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    border: 1px solid #3b82f6;
    
    &:hover {
      background: #2563eb;
      border-color: #2563eb;
    }
    
    &:disabled {
      background: #9ca3af;
      border-color: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f3f4f6;
      border-color: #9ca3af;
    }
  `}
`;

// Appointment display components
const AppointmentBlock = styled.div<{ duration: number; isToday?: boolean }>`
  position: absolute;
  left: 4px;
  right: 4px;
  top: 2px;
  height: ${(props: { duration: number; isToday?: boolean }) => (props.duration / 30) * 60 - 4}px; // 30 min slots = 60px height
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 4px;
  padding: 4px 6px;
  color: white;
  font-size: 0.75rem;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  .appointment-time {
    font-weight: 600;
    margin-bottom: 2px;
    font-size: 0.6875rem;
  }
  
  .appointment-customer {
    font-weight: 600;
    margin-bottom: 1px;
    font-size: 0.6875rem;
  }
  
  .appointment-phone {
    margin-bottom: 2px;
    opacity: 0.9;
    font-size: 0.625rem;
  }
  
  .appointment-vehicle {
    margin-bottom: 2px;
    opacity: 0.9;
    font-size: 0.625rem;
  }
  
  .appointment-services {
    opacity: 0.8;
    font-size: 0.625rem;
    line-height: 1.2;
  }
`;

// Utility functions for localStorage preferences
const getStorageKey = (userId: string, dealerId: string, key: string) => {
  return `calendar_${userId}_${dealerId}_${key}`;
};

const saveCalendarPreference = (userId: string, dealerId: string, key: string, value: any) => {
  try {
    const storageKey = getStorageKey(userId, dealerId, key);
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving calendar preference:', error);
  }
};

const getCalendarPreference = (userId: string, dealerId: string, key: string, defaultValue: any = null) => {
  try {
    const storageKey = getStorageKey(userId, dealerId, key);
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch (error) {
    console.error('Error loading calendar preference:', error);
    return defaultValue;
  }
};

const Calendar: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [locations, setLocations] = useState<DealerLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<DealerLocation | null>(null);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [locationBays, setLocationBays] = useState<ServiceBay[]>([]);
  const [enabledBays, setEnabledBays] = useState<Set<string>>(new Set());
  const [isBayDropdownOpen, setIsBayDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<'day' | 'week' | 'workweek' | 'month'>('week');
  
  // Appointment management state
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{
    date: Date;
    time: string;
    bayId: string;
  } | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [bayServices, setBayServices] = useState<DealerService[]>([]);
  const [dealerServices, setDealerServices] = useState<DealerService[]>([]);
  
  // Form state for appointment creation
  const [formData, setFormData] = useState({
    customer: {
      firstName: '',
      lastName: '',
      phone: '',
      email: ''
    },
    vehicle: {
      year: '',
      make: '',
      model: '',
      trim: ''
    },
    selectedServices: [] as string[],
    duration: 0
  });

  // Determine dealer ID based on user type
  const dealerId = user?.userType === 'admin' ? id : (user?.userType === 'dealer' ? user.dealerId : null);

  // Load saved preferences when dealerId is available
  useEffect(() => {
    if (user?.id && dealerId) {
      // Load saved calendar view preference
      const savedView = getCalendarPreference(user.id, dealerId, 'view', 'week');
      setCurrentView(savedView);
    }
  }, [user?.id, dealerId]);

  useEffect(() => {
    if (dealerId) {
      loadDealerData();
    } else {
      setIsLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    if (selectedLocation) {
      loadLocationBays();
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (dealerId) {
      loadDealerServices();
    }
  }, [dealerId]);

  useEffect(() => {
    if (dealerId && selectedLocation) {
      loadAppointments();
    }
  }, [dealerId, selectedLocation, currentDate, currentView]);

  const loadDealerData = async () => {
    if (!dealerId) return;

    try {
      setIsLoading(true);
      
      // Load dealer info
      const dealerResponse = await apiService.getDealer(dealerId);
      if (dealerResponse.success && dealerResponse.data) {
        setDealer(dealerResponse.data);
      }

      // Load locations
      const locationsResponse = await apiService.getDealerLocations(dealerId);
      if (locationsResponse.success && locationsResponse.data) {
        let availableLocations = locationsResponse.data;
        
        // Filter locations for staff users based on their assignments
        if (user?.userType === 'dealer' && (user as any).role === 'dealer_staff') {
          const userLocationIds = (user as any).locationIds || [];
          availableLocations = locationsResponse.data.filter(loc => 
            userLocationIds.includes(loc.id)
          );
        }
        
        setLocations(availableLocations);
        // Default to first location
        if (availableLocations.length > 0) {
          setSelectedLocation(availableLocations[0]);
        }
      }
    } catch (error) {
      console.error('Error loading dealer data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLocationBays = async () => {
    if (!selectedLocation) return;

    try {
      const baysResponse = await apiService.getLocationBays(selectedLocation.id);
      if (baysResponse.success && baysResponse.data) {
        setLocationBays(baysResponse.data);
        
        // Load saved bay preferences or enable all bays by default
        const allBayIds = new Set(baysResponse.data.map(bay => bay.id));
        if (user?.id && dealerId) {
          const savedBays = getCalendarPreference(user.id, dealerId, `bays_${selectedLocation.id}`, null);
          if (savedBays && Array.isArray(savedBays) && savedBays.length > 0) {
            // Validate saved bays still exist and set them
            const validSavedBays = savedBays.filter(bayId => allBayIds.has(bayId));
            setEnabledBays(new Set(validSavedBays.length > 0 ? validSavedBays : Array.from(allBayIds)));
          } else {
            // No saved preferences, enable all bays
            setEnabledBays(allBayIds);
          }
        } else {
          // No user/dealer context, enable all bays
          setEnabledBays(allBayIds);
        }
      }
    } catch (error) {
      console.error('Error loading location bays:', error);
      toast.error('Failed to load location bays');
    }
  };

  const toggleBay = (bayId: string) => {
    const newEnabledBays = new Set(enabledBays);
    if (newEnabledBays.has(bayId)) {
      newEnabledBays.delete(bayId);
    } else {
      newEnabledBays.add(bayId);
    }
    setEnabledBays(newEnabledBays);
    
    // Save bay preferences
    if (user?.id && dealerId && selectedLocation) {
      saveCalendarPreference(user.id, dealerId, `bays_${selectedLocation.id}`, Array.from(newEnabledBays));
    }
  };

  const selectAllBays = () => {
    const allBayIds = new Set(locationBays.map(bay => bay.id));
    setEnabledBays(allBayIds);
    
    // Save bay preferences
    if (user?.id && dealerId && selectedLocation) {
      saveCalendarPreference(user.id, dealerId, `bays_${selectedLocation.id}`, Array.from(allBayIds));
    }
  };

  const unselectAllBays = () => {
    setEnabledBays(new Set());
    
    // Save bay preferences
    if (user?.id && dealerId && selectedLocation) {
      saveCalendarPreference(user.id, dealerId, `bays_${selectedLocation.id}`, []);
    }
  };

  // Load dealer services
  const loadDealerServices = async () => {
    if (!dealerId) return;
    try {
      const response = await apiService.getDealerServices(dealerId);
      if (response.success && response.data) {
        setDealerServices(response.data);
      }
    } catch (error) {
      console.error('Error loading dealer services:', error);
    }
  };

  // Load services assigned to a specific bay
  const loadBayServices = async (bayId: string) => {
    try {
      // If no dealer services loaded yet, wait a moment and try again
      if (dealerServices.length === 0) {
        await loadDealerServices();
      }
      
      const response = await apiService.getBayServices(bayId);
      
      if (response.success && response.data && response.data.length > 0) {
        // Filter dealer services to only include those assigned to this bay
        const assignedServices = dealerServices.filter(service => 
          response.data?.some((bayService: any) => bayService.serviceId === service.id)
        );
        setBayServices(assignedServices);
      } else {
        // Fallback: show all dealer services if no bay-specific assignments exist
        setBayServices(dealerServices);
      }
    } catch (error) {
      console.error('Error loading bay services:', error);
      // Fallback: show all dealer services if API call fails
      setBayServices(dealerServices);
    }
  };

  // Handle time slot click
  const handleTimeSlotClick = (date: Date, time: string, bayId: string) => {
    setSelectedSlot({ date, time, bayId });
    setEditingAppointment(null);
    setFormData({
      customer: { firstName: '', lastName: '', phone: '', email: '' },
      vehicle: { year: '', make: '', model: '', trim: '' },
      selectedServices: [],
      duration: 0
    });
    loadBayServices(bayId);
    setIsModalOpen(true);
  };

  // Handle appointment click for editing
  const handleAppointmentClick = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    
    // Parse the appointment date more safely
    const appointmentDate = typeof appointment.date === 'string' 
      ? appointment.date.includes('T') 
        ? new Date(appointment.date) 
        : new Date(appointment.date + 'T00:00:00')
      : new Date(appointment.date);
    
    setSelectedSlot({
      date: appointmentDate,
      time: appointment.startTime,
      bayId: appointment.bayId
    });
    
    // Pre-populate form with appointment data
    setFormData({
      customer: {
        firstName: appointment.customer.firstName,
        lastName: appointment.customer.lastName,
        phone: appointment.customer.phone || '',
        email: appointment.customer.email || ''
      },
      vehicle: {
        year: appointment.vehicle.year || '',
        make: appointment.vehicle.make || '',
        model: appointment.vehicle.model || '',
        trim: appointment.vehicle.trim || ''
      },
      selectedServices: appointment.services.map(s => s.serviceId),
      duration: appointment.duration
    });
    
    loadBayServices(appointment.bayId);
    setIsModalOpen(true);
  };

  // Handle service selection
  const toggleService = (serviceId: string) => {
    const newSelectedServices = formData.selectedServices.includes(serviceId)
      ? formData.selectedServices.filter(id => id !== serviceId)
      : [...formData.selectedServices, serviceId];
    
    // Calculate total duration
    const totalDuration = newSelectedServices.reduce((total, id) => {
      const service = bayServices.find(s => s.id === id);
      return total + (service?.durationMinutes || 0);
    }, 0);

    setFormData(prev => ({
      ...prev,
      selectedServices: newSelectedServices,
      duration: totalDuration
    }));
  };

  // Handle form field changes
  const updateFormField = (section: 'customer' | 'vehicle', field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Handle duration override
  const updateDuration = (duration: number) => {
    setFormData(prev => ({ ...prev, duration }));
  };

  // Load appointments from database
  const loadAppointments = async () => {
    if (!dealerId || !selectedLocation) return;
    
    try {
      // Get date range based on current view
      let startDate: Date, endDate: Date;
      const viewDays = getCurrentViewDays();
      
      if (currentView === 'month') {
        startDate = viewDays[0];
        endDate = viewDays[viewDays.length - 1];
      } else {
        startDate = viewDays[0];
        endDate = viewDays[viewDays.length - 1];
      }

      // Load appointments for the date range
      const promises = [];
      const currentDay = new Date(startDate);
      
      while (currentDay <= endDate) {
        const dateStr = currentDay.toISOString().split('T')[0];
        promises.push(
          apiService.getLocationAppointments(selectedLocation.id, dateStr)
        );
        currentDay.setDate(currentDay.getDate() + 1);
      }

      const responses = await Promise.all(promises);
      const allAppointments: Appointment[] = [];
      
      responses.forEach(response => {
        if (response.success && response.data) {
          allAppointments.push(...response.data);
        }
      });

      setAppointments(allAppointments);
      
    } catch (error: any) {
      console.error('Error loading appointments:', error);
      toast.error('Failed to load appointments');
      
      // Set empty appointments array on error
      setAppointments([]);
    }
  };

  // Update appointment
  const updateAppointment = async () => {
    if (!editingAppointment || !selectedSlot || !dealerId || !selectedLocation || formData.selectedServices.length === 0 || !formData.customer.firstName || !formData.customer.lastName) {
      toast.error('Please fill in required fields and select at least one service');
      return;
    }

    try {
      const selectedServiceObjects = bayServices.filter(service => 
        formData.selectedServices.includes(service.id)
      );

      // Validate the date before using it
      if (!selectedSlot.date || isNaN(selectedSlot.date.getTime())) {
        console.error('Invalid date in selectedSlot:', selectedSlot.date);
        toast.error('Invalid date. Please try again.');
        return;
      }

      const appointmentData = {
        id: editingAppointment.id,
        dealerId,
        locationId: selectedLocation.id,
        bayId: selectedSlot.bayId,
        date: selectedSlot.date.toISOString().split('T')[0],
        startTime: selectedSlot.time,
        duration: formData.duration,
        customer: formData.customer,
        vehicle: formData.vehicle,
        services: selectedServiceObjects.map(service => ({
          serviceId: service.id,
          serviceName: service.name,
          duration: service.durationMinutes,
          price: service.price
        })),
        status: editingAppointment.status
      };

      const response = await apiService.updateAppointment(editingAppointment.id, appointmentData);
      
      if (response.success && response.data) {
        
        // Close modal and reset form first
        setIsModalOpen(false);
        setSelectedSlot(null);
        setEditingAppointment(null);
        setFormData({
          customer: { firstName: '', lastName: '', phone: '', email: '' },
          vehicle: { year: '', make: '', model: '', trim: '' },
          selectedServices: [],
          duration: 0
        });
        
        // Reload appointments to ensure the updated appointment is displayed
        await loadAppointments();
        
        toast.success('Appointment updated successfully');
      } else {
        toast.error('Failed to update appointment: ' + (response.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error updating appointment:', error);
      
      if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication error. Please log in again.');
      } else {
        toast.error('Failed to update appointment. Please try again.');
      }
    }
  };

  // Delete appointment
  const deleteAppointment = async () => {
    if (!editingAppointment) return;

    if (!window.confirm('Are you sure you want to delete this appointment? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.deleteAppointment(editingAppointment.id);
      
      if (response.success) {
        
        // Close modal and reset form
        setIsModalOpen(false);
        setSelectedSlot(null);
        setEditingAppointment(null);
        setFormData({
          customer: { firstName: '', lastName: '', phone: '', email: '' },
          vehicle: { year: '', make: '', model: '', trim: '' },
          selectedServices: [],
          duration: 0
        });
        
        // Reload appointments to ensure the deleted appointment is removed
        await loadAppointments();
        
        toast.success('Appointment deleted successfully');
      } else {
        toast.error('Failed to delete appointment: ' + (response.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error deleting appointment:', error);
      toast.error('Failed to delete appointment. Please try again.');
    }
  };

  // Create appointment
  const createAppointment = async () => {
    if (!selectedSlot || !dealerId || !selectedLocation || formData.selectedServices.length === 0 || !formData.customer.firstName || !formData.customer.lastName) {
      toast.error('Please fill in required fields and select at least one service');
      return;
    }

    try {
      const selectedServiceObjects = bayServices.filter(service => 
        formData.selectedServices.includes(service.id)
      );

      const appointmentData = {
        dealerId,
        locationId: selectedLocation.id,
        bayId: selectedSlot.bayId,
        date: selectedSlot.date.toISOString().split('T')[0], // Convert to ISO date string
        startTime: selectedSlot.time,
        duration: formData.duration,
        customer: formData.customer,
        vehicle: formData.vehicle,
        services: selectedServiceObjects.map(service => ({
          serviceId: service.id,
          serviceName: service.name,
          duration: service.durationMinutes,
          price: service.price
        }))
      };

      const response = await apiService.createAppointment(appointmentData);
      
      if (response.success && response.data) {
        
        // Close modal and reset form first
        setIsModalOpen(false);
        setSelectedSlot(null);
        setFormData({
          customer: { firstName: '', lastName: '', phone: '', email: '' },
          vehicle: { year: '', make: '', model: '', trim: '' },
          selectedServices: [],
          duration: 0
        });
        
        // Reload appointments to ensure the new appointment is displayed
        await loadAppointments();
        
        toast.success('Appointment created successfully');
      } else {
        toast.error('Failed to create appointment: ' + (response.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      
      if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication error. Please log in again.');
      } else {
        toast.error('Failed to create appointment. Please try again.');
      }
    }
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSlot(null);
    setEditingAppointment(null);
    setBayServices([]);
    setFormData({
      customer: { firstName: '', lastName: '', phone: '', email: '' },
      vehicle: { year: '', make: '', model: '', trim: '' },
      selectedServices: [],
      duration: 0
    });
  };

  const handleBack = () => {
    if (user?.userType === 'dealer') {
      navigate('/dealer-dashboard');
    } else {
      navigate('/dealers');
    }
  };

  const getWeekDays = (date: Date): Date[] => {
    const week = [];
    const startDate = new Date(date);
    const day = startDate.getDay();
    const diff = startDate.getDate() - day; // First day is Sunday
    
    for (let i = 0; i < 7; i++) {
      const weekDay = new Date(date.getFullYear(), date.getMonth(), diff + i);
      week.push(weekDay);
    }
    
    return week;
  };

  const getWorkWeekDays = (date: Date): Date[] => {
    const week = [];
    const startDate = new Date(date);
    const day = startDate.getDay();
    const diff = startDate.getDate() - day + 1; // Start on Monday
    
    for (let i = 0; i < 5; i++) { // Monday to Friday
      const weekDay = new Date(date.getFullYear(), date.getMonth(), diff + i);
      week.push(weekDay);
    }
    
    return week;
  };

  const getDayView = (date: Date): Date[] => {
    return [new Date(date)];
  };

  const getMonthDays = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // Get first day of month and determine starting date (including previous month days)
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    
    // Start from Sunday of the week containing the first day
    startDate.setDate(firstDay.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDay = new Date(startDate);
    
    // Generate 42 days (6 weeks) to fill the calendar grid
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  const getCurrentViewDays = (): Date[] => {
    switch (currentView) {
      case 'day':
        return getDayView(currentDate);
      case 'week':
        return getWeekDays(currentDate);
      case 'workweek':
        return getWorkWeekDays(currentDate);
      case 'month':
        return getMonthDays(currentDate);
      default:
        return getWeekDays(currentDate);
    }
  };

  const formatDateRange = (date: Date): string => {
    switch (currentView) {
      case 'day':
        return date.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      case 'week':
      case 'workweek':
        const viewDays = currentView === 'week' ? getWeekDays(date) : getWorkWeekDays(date);
        const start = viewDays[0];
        const end = viewDays[viewDays.length - 1];
        
        const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
        const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
        const year = start.getFullYear();
        
        if (start.getMonth() === end.getMonth()) {
          return `${startMonth} ${start.getDate()} – ${end.getDate()}, ${year}`;
        } else {
          return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${year}`;
        }
      case 'month':
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      default:
        return '';
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    
    switch (currentView) {
      case 'day':
        newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'week':
      case 'workweek':
        newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }
    
    setCurrentDate(newDate);
  };

  const goToDate = (date: Date) => {
    setCurrentDate(new Date(date));
    handleViewChange('day');
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle view change with preference saving
  const handleViewChange = (view: 'day' | 'week' | 'workweek' | 'month') => {
    setCurrentView(view);
    
    // Save view preference
    if (user?.id && dealerId) {
      saveCalendarPreference(user.id, dealerId, 'view', view);
    }
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const generateTimeSlots = (): string[] => {
    const slots = [];
    for (let hour = 7; hour <= 18; hour++) {
      slots.push(`${hour === 12 ? 12 : hour % 12}${hour < 12 ? 'am' : 'pm'}`);
      if (hour < 18) {
        slots.push(`${hour === 12 ? 12 : hour % 12}:30${hour < 12 ? 'am' : 'pm'}`);
      }
    }
    return slots;
  };

  // Get appointment count for a specific date
  const getAppointmentCount = (date: Date): number => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => {
      const aptDate = apt.date.includes('T') ? apt.date.split('T')[0] : apt.date;
      return aptDate === dateStr;
    }).length;
  };

  // Get appointments for a specific time slot
  const getAppointmentsForSlot = (date: Date, time: string, bayId: string): Appointment[] => {
    const dateStr = date.toISOString().split('T')[0];
    
    const matchingAppointments = appointments.filter(apt => {
      // Ensure both dates are in the same format for comparison
      const aptDate = apt.date.includes('T') ? apt.date.split('T')[0] : apt.date;
      return apt.bayId === bayId &&
        aptDate === dateStr &&
        apt.startTime === time;
    });
    
    return matchingAppointments;
  };


  // Check if a time slot is occupied by a continuing appointment
  const isTimeSlotOccupied = (date: Date, time: string, bayId: string): boolean => {
    const dateStr = date.toISOString().split('T')[0];
    
    return appointments.some(apt => {
      const aptDate = apt.date.includes('T') ? apt.date.split('T')[0] : apt.date;
      if (apt.bayId !== bayId || aptDate !== dateStr) return false;
      
      const appointmentStartTime = timeToMinutes(apt.startTime);
      const appointmentEndTime = appointmentStartTime + apt.duration;
      const currentSlotTime = timeToMinutes(time);
      
      // Check if current time slot falls within the appointment duration
      return currentSlotTime >= appointmentStartTime && currentSlotTime < appointmentEndTime;
    });
  };

  // Convert time string (e.g., "8:00am" or "9am") to minutes since midnight
  const timeToMinutes = (timeStr: string): number => {
    // Handle both formats: "9am" and "9:30am"
    const matchWithMinutes = timeStr.match(/(\d+):(\d+)(am|pm)/i);
    const matchHourOnly = timeStr.match(/(\d+)(am|pm)/i);
    
    let hours: number;
    let minutes: number;
    let ampm: string;
    
    if (matchWithMinutes) {
      hours = parseInt(matchWithMinutes[1]);
      minutes = parseInt(matchWithMinutes[2]);
      ampm = matchWithMinutes[3].toLowerCase();
    } else if (matchHourOnly) {
      hours = parseInt(matchHourOnly[1]);
      minutes = 0;
      ampm = matchHourOnly[2].toLowerCase();
    } else {
      console.warn('Unable to parse time string:', timeStr);
      return 0;
    }
    
    if (ampm === 'pm' && hours !== 12) hours += 12;
    if (ampm === 'am' && hours === 12) hours = 0;
    
    return hours * 60 + minutes;
  };

  // Convert minutes since midnight to time string
  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')}${ampm}`;
  };

  // Get appointments that start at a specific time slot
  const getAppointmentsStartingAtSlot = (date: Date, time: string, bayId: string): Appointment[] => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => {
      const aptDate = apt.date.includes('T') ? apt.date.split('T')[0] : apt.date;
      return apt.bayId === bayId &&
        aptDate === dateStr &&
        apt.startTime === time;
    });
  };

  const renderMonthView = () => {
    const monthDays = getMonthDays(currentDate);
    const currentMonth = currentDate.getMonth();
    
    return (
      <CalendarContainer>
        <MonthGrid>
          {monthDays.map((day, index) => {
            const isCurrentMonth = day.getMonth() === currentMonth;
            const appointmentCount = getAppointmentCount(day);
            
            return (
              <MonthDay
                key={index}
                isCurrentMonth={isCurrentMonth}
                isToday={isToday(day)}
                hasAppointments={appointmentCount > 0}
                onClick={() => goToDate(day)}
              >
                <div className="day-number">{day.getDate()}</div>
                {appointmentCount > 0 && (
                  <div className="appointment-indicator">
                    {appointmentCount} {appointmentCount === 1 ? 'appt' : 'apts'}
                  </div>
                )}
              </MonthDay>
            );
          })}
        </MonthGrid>
      </CalendarContainer>
    );
  };

  const renderTimeGridView = () => {
    return (
      <CalendarContainer>
        <CalendarHeader enabledBaysCount={enabledBays.size}>
          {/* Day headers row */}
          <HeaderRow enabledBaysCount={enabledBays.size} dayCount={viewDays.length}>
            <TimeColumn></TimeColumn>
            {viewDays.map((day, dayIndex) => (
              <DayHeaderCell 
                key={dayIndex} 
                isToday={isToday(day)} 
                bayCount={Math.max(1, enabledBays.size)}
                onClick={() => goToDate(day)}
                style={{ cursor: 'pointer' }}
              >
                <div className="day-name">
                  {day.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="day-number">
                  {day.getDate()}
                </div>
              </DayHeaderCell>
            ))}
          </HeaderRow>
          
          {/* Bay headers row (show when bays are available) */}
          {enabledBays.size > 0 && (
            <HeaderRow enabledBaysCount={enabledBays.size} dayCount={viewDays.length}>
              <TimeColumn></TimeColumn>
              {viewDays.map((day, dayIndex) => (
                <React.Fragment key={dayIndex}>
                  {locationBays
                    .filter(bay => enabledBays.has(bay.id))
                    .sort((a, b) => a.bayNumber - b.bayNumber)
                    .map((bay, bayIndex) => (
                      <BayHeaderCell 
                        key={bay.id}
                        isToday={isToday(day)}
                        isLastBayInDay={bayIndex === enabledBays.size - 1}
                      >
                        Bay {bay.bayNumber}
                      </BayHeaderCell>
                    ))
                  }
                </React.Fragment>
              ))}
            </HeaderRow>
          )}
        </CalendarHeader>

        <CalendarGrid enabledBaysCount={enabledBays.size} dayCount={viewDays.length}>
          {timeSlots.map((time, timeIndex) => (
            <React.Fragment key={timeIndex}>
              <TimeSlot>{time}</TimeSlot>
              {viewDays.map((day, dayIndex) => (
                <React.Fragment key={`${timeIndex}-${dayIndex}`}>
                  {locationBays
                    .filter(bay => enabledBays.has(bay.id))
                    .sort((a, b) => a.bayNumber - b.bayNumber)
                    .map((bay, bayIndex) => (
                        <BayColumn
                          key={`${day.getTime()}-${bay.id}`}
                          isToday={isToday(day)}
                          isLastBay={dayIndex === viewDays.length - 1 && bayIndex === enabledBays.size - 1}
                          onClick={() => !isTimeSlotOccupied(day, time, bay.id) && handleTimeSlotClick(day, time, bay.id)}
                          style={{ 
                            cursor: isTimeSlotOccupied(day, time, bay.id) ? 'default' : 'pointer',
                            position: 'relative'
                          }}
                        >
                          <div className="bay-label">
                            {bay.bayNumber}
                          </div>
                          {getAppointmentsStartingAtSlot(day, time, bay.id).map(appointment => (
                            <AppointmentBlock 
                              key={appointment.id} 
                              duration={appointment.duration}
                              isToday={isToday(day)}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation(); // Prevent triggering the time slot click
                                handleAppointmentClick(appointment);
                              }}
                            >
                              <div className="appointment-time">
                                {appointment.startTime} - {minutesToTime(timeToMinutes(appointment.startTime) + appointment.duration)}
                              </div>
                              <div className="appointment-customer">
                                {appointment.customer.lastName}
                              </div>
                              <div className="appointment-phone">
                                {appointment.customer.phone}
                              </div>
                              <div className="appointment-vehicle">
                                {appointment.vehicle.year} {appointment.vehicle.make} {appointment.vehicle.model}
                              </div>
                              <div className="appointment-services">
                                {appointment.services.map(s => s.serviceName).join(', ')}
                              </div>
                            </AppointmentBlock>
                          ))}
                        </BayColumn>
                    ))
                  }
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </CalendarGrid>
      </CalendarContainer>
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  if (!dealerId || !dealer) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          {user?.userType === 'admin' ? 'Dealer not found' : 'Access denied'}
        </div>
      </PageContainer>
    );
  }

  // Show third-party integration message if configured
  if (dealer.useThirdPartyIntegration && dealer.thirdPartyProvider) {
    return (
      <PageContainer>
        <PageHeader>
          <div className="header-left">
            {user?.userType === 'admin' && (
              <BackButton onClick={handleBack}>
                <ArrowLeft />
                Back
              </BackButton>
            )}
            <h1>
              <CalendarIcon />
              Calendar
            </h1>
          </div>
        </PageHeader>

        <DealerInfo>
          <div className="dealer-name">
            <MapPin size={16} />
            {dealer.name}
          </div>
          <div className="dealer-details">
            Calendar management for this dealership
          </div>
        </DealerInfo>

        <ThirdPartyBanner>
          <ExternalLink className="icon" size={20} />
          <div className="content">
            <div className="title">Third-Party Integration Active</div>
            <div className="description">
              This account is using {dealer.thirdPartyProvider === 'tireworkshd' ? 'TireWorks HD' : dealer.thirdPartyProvider} to manage appointments.
            </div>
          </div>
        </ThirdPartyBanner>
      </PageContainer>
    );
  }

  if (locations.length === 0) {
    return (
      <PageContainer>
        <PageHeader>
          <div className="header-left">
            {user?.userType === 'admin' && (
              <BackButton onClick={handleBack}>
                <ArrowLeft />
                Back
              </BackButton>
            )}
            <h1>
              <CalendarIcon />
              Calendar
            </h1>
          </div>
        </PageHeader>

        <DealerInfo>
          <div className="dealer-name">
            <MapPin size={16} />
            {dealer.name}
          </div>
          <div className="dealer-details">
            No locations found. Add locations to manage calendars.
          </div>
        </DealerInfo>
      </PageContainer>
    );
  }

  const viewDays = getCurrentViewDays();
  const timeSlots = generateTimeSlots();

  return (
    <PageContainer>
      <PageHeader>
        <div className="header-left">
          {user?.userType === 'admin' && (
            <BackButton onClick={handleBack}>
              <ArrowLeft />
              Back
            </BackButton>
          )}
          <h1>
            <CalendarIcon />
            Calendar
          </h1>
        </div>
      </PageHeader>

      <DealerInfo>
        <div className="dealer-name">
          <MapPin size={16} />
          {dealer.name}
        </div>
        <div className="dealer-details">
          Calendar management for this dealership
        </div>
      </DealerInfo>

      {/* Location and Bay Selectors */}
      <SelectorsRow>
        {/* Location Selector - only show if multiple locations */}
        {locations.length > 1 && (
          <div className="selector-group">
            <div className="selector-label">Location</div>
            <LocationDropdown>
              <DropdownButton onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}>
                <div className="location-info">
                  <MapPin size={16} />
                  <span className="location-name">{selectedLocation?.name || 'Select Location'}</span>
                </div>
                <ChevronDown />
              </DropdownButton>
              <DropdownMenu isOpen={isLocationDropdownOpen}>
                {locations.map((location) => (
                  <DropdownItem
                    key={location.id}
                    onClick={() => {
                      setSelectedLocation(location);
                      setIsLocationDropdownOpen(false);
                    }}
                  >
                    <MapPin size={16} />
                    <div>
                      <div className="location-name">{location.name}</div>
                      {location.address && (
                        <div className="location-address">{location.address}</div>
                      )}
                    </div>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </LocationDropdown>
          </div>
        )}

        {/* Bay Selector - show if location has bays */}
        {locationBays.length > 0 && (
          <div className="selector-group">
            <div className="selector-label">Service Bays</div>
            <BaySelector>
              <BayDropdownButton onClick={() => setIsBayDropdownOpen(!isBayDropdownOpen)}>
                <div className="bay-info">
                  <Settings size={16} />
                  <span className="bay-count">{enabledBays.size}</span>
                  <span className="bay-text">of {locationBays.length} bays</span>
                </div>
                <ChevronDown />
              </BayDropdownButton>
              <BayDropdownMenu isOpen={isBayDropdownOpen}>
                <BayDropdownHeader>
                  <div className="bay-controls">
                    <div className="bay-title">Select Bays to Display</div>
                    <div className="bay-actions">
                      <button onClick={selectAllBays}>All</button>
                      <button onClick={unselectAllBays}>None</button>
                    </div>
                  </div>
                </BayDropdownHeader>
                {locationBays.map((bay) => (
                  <BayDropdownItem
                    key={bay.id}
                    isSelected={enabledBays.has(bay.id)}
                    onClick={() => toggleBay(bay.id)}
                  >
                    <div className="checkbox">
                      {enabledBays.has(bay.id) && <Check />}
                    </div>
                    <div className="bay-details">
                      <div className="bay-name">Bay {bay.bayNumber}: {bay.bayName}</div>
                      {bay.description && (
                        <div className="bay-description">{bay.description}</div>
                      )}
                    </div>
                  </BayDropdownItem>
                ))}
              </BayDropdownMenu>
            </BaySelector>
          </div>
        )}
      </SelectorsRow>

      {selectedLocation && (
        <>
          <CalendarControls>
            <div className="date-navigation">
              <NavButton onClick={() => navigateDate('prev')}>
                <ChevronLeft />
              </NavButton>
              <div className="current-week">{formatDateRange(currentDate)}</div>
              <NavButton onClick={() => navigateDate('next')}>
                <ChevronRight />
              </NavButton>
            </div>
            <div className="view-controls">
              <ViewButton 
                isActive={currentView === 'day'} 
                onClick={() => handleViewChange('day')}
              >
                Day
              </ViewButton>
              <ViewButton 
                isActive={currentView === 'week'} 
                onClick={() => handleViewChange('week')}
              >
                Week
              </ViewButton>
              <ViewButton 
                isActive={currentView === 'workweek'} 
                onClick={() => handleViewChange('workweek')}
              >
                Work Week
              </ViewButton>
              <ViewButton 
                isActive={currentView === 'month'} 
                onClick={() => handleViewChange('month')}
              >
                Month
              </ViewButton>
            </div>
          </CalendarControls>

          {enabledBays.size === 0 ? (
            <div style={{ position: 'relative' }}>
              <EmptyStateContainer>
                <CalendarContainer>
                  <CalendarHeader enabledBaysCount={1}>
                    {/* Day headers row */}
                    <HeaderRow enabledBaysCount={1} dayCount={viewDays.length}>
                      <TimeColumn></TimeColumn>
                      {viewDays.map((day, dayIndex) => (
                        <DayHeaderCell 
                          key={dayIndex} 
                          isToday={isToday(day)} 
                          bayCount={1}
                        >
                          <div className="day-name">
                            {day.toLocaleDateString('en-US', { weekday: 'short' })}
                          </div>
                          <div className="day-number">
                            {day.getDate()}
                          </div>
                        </DayHeaderCell>
                      ))}
                    </HeaderRow>
                    
                    {/* Empty bay headers row */}
                    <HeaderRow enabledBaysCount={1} dayCount={viewDays.length}>
                      <TimeColumn></TimeColumn>
                      {viewDays.map((day, dayIndex) => (
                        <BayHeaderCell 
                          key={dayIndex}
                          isToday={isToday(day)}
                          isLastBayInDay={true}
                        >
                          Bay -
                        </BayHeaderCell>
                      ))}
                    </HeaderRow>
                  </CalendarHeader>

                  <CalendarGrid enabledBaysCount={1} dayCount={viewDays.length}>
                    {timeSlots.map((time, timeIndex) => (
                      <React.Fragment key={timeIndex}>
                        <TimeSlot>{time}</TimeSlot>
                        {viewDays.map((day, dayIndex) => (
                          <BayColumn 
                            key={`${timeIndex}-${dayIndex}`}
                            isToday={isToday(day)} 
                            isLastBay={dayIndex === 6}
                          />
                        ))}
                      </React.Fragment>
                    ))}
                  </CalendarGrid>
                </CalendarContainer>
              </EmptyStateContainer>
              
              <EmptyStateMessage>
                <div className="message-icon">
                  <AlertCircle size={24} />
                </div>
                <div className="message-title">No Bays Selected</div>
                <div className="message-text">Please select a bay above</div>
              </EmptyStateMessage>
            </div>
          ) : (
            currentView === 'month' ? renderMonthView() : renderTimeGridView()
          )}
        </>
      )}
      
      {/* Create Appointment Modal */}
      <ModalOverlay isOpen={isModalOpen} onClick={(e) => e.target === e.currentTarget && closeModal()}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <h2>{editingAppointment ? 'Edit Appointment' : 'Create Appointment'}</h2>
            <CloseButton onClick={closeModal}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          {selectedSlot && (
            <>
              <FormSection>
                <div className="section-title">
                  <CalendarIcon size={20} />
                  Appointment Details
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '16px' }}>
                  {selectedSlot.date.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })} at {selectedSlot.time}
                  {' • '}Bay {locationBays.find(b => b.id === selectedSlot.bayId)?.bayNumber}
                </div>
              </FormSection>

              <FormSection>
                <div className="section-title">Customer Information</div>
                <FormGrid>
                  <FormField>
                    <label>First Name *</label>
                    <input
                      type="text"
                      placeholder="Enter first name"
                      value={formData.customer.firstName}
                      onChange={(e) => updateFormField('customer', 'firstName', e.target.value)}
                    />
                  </FormField>
                  <FormField>
                    <label>Last Name *</label>
                    <input
                      type="text"
                      placeholder="Enter last name"
                      value={formData.customer.lastName}
                      onChange={(e) => updateFormField('customer', 'lastName', e.target.value)}
                    />
                  </FormField>
                  <FormField>
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      placeholder="Enter phone number"
                      value={formData.customer.phone}
                      onChange={(e) => updateFormField('customer', 'phone', e.target.value)}
                    />
                  </FormField>
                  <FormField>
                    <label>Email Address</label>
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={formData.customer.email}
                      onChange={(e) => updateFormField('customer', 'email', e.target.value)}
                    />
                  </FormField>
                </FormGrid>
              </FormSection>

              <FormSection>
                <div className="section-title">Vehicle Details</div>
                <FormGrid>
                  <FormField>
                    <label>Year</label>
                    <input
                      type="text"
                      placeholder="e.g. 2020"
                      value={formData.vehicle.year}
                      onChange={(e) => updateFormField('vehicle', 'year', e.target.value)}
                    />
                  </FormField>
                  <FormField>
                    <label>Make</label>
                    <input
                      type="text"
                      placeholder="e.g. Honda"
                      value={formData.vehicle.make}
                      onChange={(e) => updateFormField('vehicle', 'make', e.target.value)}
                    />
                  </FormField>
                  <FormField>
                    <label>Model</label>
                    <input
                      type="text"
                      placeholder="e.g. Civic"
                      value={formData.vehicle.model}
                      onChange={(e) => updateFormField('vehicle', 'model', e.target.value)}
                    />
                  </FormField>
                  <FormField>
                    <label>Trim</label>
                    <input
                      type="text"
                      placeholder="e.g. LX"
                      value={formData.vehicle.trim}
                      onChange={(e) => updateFormField('vehicle', 'trim', e.target.value)}
                    />
                  </FormField>
                </FormGrid>
              </FormSection>

              <FormSection>
                <div className="section-title">Services</div>
                <ServiceList>
                  {bayServices.length === 0 ? (
                    <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
                      No services assigned to this bay
                    </div>
                  ) : (
                    bayServices.map((service) => (
                    <ServiceItem
                      key={service.id}
                      isSelected={formData.selectedServices.includes(service.id)}
                      onClick={() => toggleService(service.id)}
                    >
                      <div className="checkbox">
                        {formData.selectedServices.includes(service.id) && <Check />}
                      </div>
                      <div className="service-info">
                        <div className="service-name">{service.name}</div>
                        <div className="service-details">
                          <span>{service.durationMinutes} min</span>
                          {service.price && <span>${service.price}</span>}
                          {service.description && <span>{service.description}</span>}
                        </div>
                      </div>
                    </ServiceItem>
                    ))
                  )}
                </ServiceList>
                <InfoMessage>
                  {bayServices.length === dealerServices.length 
                    ? "Showing all available services (no bay-specific assignments found)."
                    : "Displaying services assigned to selected bay."
                  }
                </InfoMessage>
              </FormSection>

              <FormSection>
                <div className="section-title">Duration</div>
                <FormField>
                  <label>Total Duration (minutes)</label>
                  <input
                    type="number"
                    placeholder="Auto-calculated from services"
                    value={formData.duration}
                    onChange={(e) => updateDuration(parseInt(e.target.value) || 0)}
                  />
                </FormField>
              </FormSection>

              <ModalActions>
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                {editingAppointment && (
                  <Button 
                    variant="secondary" 
                    onClick={deleteAppointment}
                    style={{ backgroundColor: '#dc2626', borderColor: '#dc2626', color: 'white' }}
                  >
                    Delete Appointment
                  </Button>
                )}
                <Button 
                  variant="primary" 
                  onClick={editingAppointment ? updateAppointment : createAppointment}
                  disabled={!formData.customer.firstName || !formData.customer.lastName || formData.selectedServices.length === 0}
                >
                  {editingAppointment ? 'Update Appointment' : 'Add Appointment'}
                </Button>
              </ModalActions>
            </>
          )}
        </ModalContent>
      </ModalOverlay>
    </PageContainer>
  );
};

export default Calendar;